/**
 * Hook WebSocket per push real-time dal backend.
 * Auto-riconnessione con backoff esponenziale.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenStore, API_URL } from '@/lib/api/client';
import { toast } from 'sonner';

export type WsEvent =
  | { type: 'message:new'; accountId: string; messageId: string; threadId: string }
  | { type: 'thread:updated'; threadId: string }
  | { type: 'sync:status'; accountId: string; status: string }
  | { type: 'reminder:trigger'; reminderId: string; threadId: string; reason?: string }
  | { type: 'draft:saved'; draftId: string }
  | { type: 'ai:job-complete'; jobId: string; jobType: string };

function buildWsUrl(): string {
  const token = tokenStore.getAccess();
  if (!token) return '';
  const base = API_URL.replace(/^http/, 'ws').replace(/\/api\/v1$/, '');
  return `${base}/ws?token=${encodeURIComponent(token)}`;
}

export function useWebSocket(): void {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEvent = useCallback(
    (ev: WsEvent) => {
      switch (ev.type) {
        case 'message:new':
          qc.invalidateQueries({ queryKey: ['messages'] });
          qc.invalidateQueries({ queryKey: ['thread', ev.threadId] });
          if (Notification.permission === 'granted' && document.hidden) {
            new Notification('Nuovo messaggio', {
              body: 'Hai ricevuto una nuova email',
              icon: '/favicon.svg',
              tag: ev.messageId,
            });
          }
          break;
        case 'thread:updated':
          qc.invalidateQueries({ queryKey: ['messages'] });
          qc.invalidateQueries({ queryKey: ['thread', ev.threadId] });
          break;
        case 'sync:status':
          qc.invalidateQueries({ queryKey: ['accounts'] });
          break;
        case 'reminder:trigger':
          qc.invalidateQueries({ queryKey: ['reminders'] });
          toast(`Promemoria${ev.reason ? `: ${ev.reason}` : ''}`, {
            description: 'Hai un thread da rivedere',
          });
          break;
        case 'draft:saved':
          qc.invalidateQueries({ queryKey: ['drafts'] });
          break;
        case 'ai:job-complete':
          qc.invalidateQueries({ queryKey: ['ai-jobs'] });
          break;
      }
    },
    [qc]
  );

  const connect = useCallback(() => {
    const url = buildWsUrl();
    if (!url) return;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data) as WsEvent;
          handleEvent(data);
        } catch {
          // ignore malformed
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        // Backoff: 1s, 2s, 4s, 8s, 16s, 30s max
        const attempt = Math.min(reconnectAttemptsRef.current, 5);
        const delay = Math.min(1000 * 2 ** attempt, 30_000);
        reconnectAttemptsRef.current++;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* */ }
      };
    } catch {
      // retry
      reconnectTimerRef.current = setTimeout(connect, 5000);
    }
  }, [handleEvent]);

  useEffect(() => {
    if (!tokenStore.getAccess()) return;
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // disable auto-reconnect on unmount
        wsRef.current.close();
      }
    };
  }, [connect]);
}
